import { Divider, Drawer, Grid, Typography } from '@material-ui/core';
import { InsertDriveFile } from '@material-ui/icons';
import GetApp from '@material-ui/icons/GetApp';
import { CompraVendaDocumento } from 'common/genericTypes';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { Button, MdSpace, SmSpace } from 'm2m.components';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { POSTTransferenciaLoteComprovantes } from 'requests/operacao';
import { addAlert } from 'store/modules/snackBar';

interface GestaoDocumentosTransferenciaDrawerProps {
  operacaoId?: string;
  codigoInstrumento?: string;
  documentos?: Array<CompraVendaDocumento>;
  opened: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

export const GestaoDocumentosTransferenciaDrawer: React.FC<GestaoDocumentosTransferenciaDrawerProps> = (props) => {
  const { operacaoId, codigoInstrumento, documentos, opened, onClose, onUploadSuccess } = props;
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const dispatch = useDispatch();

  const handleAttachmentsChange = (newAttachments: Attachment[]) => {
    setAttachments((prev) => prev.concat(newAttachments));
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleUpload = async () => {
    if (!operacaoId || attachments.length === 0) return;

    setUploading(true);
    try {
      const itens = attachments
        .filter((attachment) => attachment.file)
        .map((attachment) => ({ operacaoId: operacaoId!, arquivo: attachment.file! }));

      await POSTTransferenciaLoteComprovantes(dispatch, itens, true);
      dispatch(addAlert({ severity: 'success', message: 'Documento(s) anexado(s) com sucesso.' }));
      setAttachments([]);
      setUploadKey((prev) => prev + 1);
      onUploadSuccess();
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData?.itens?.length > 0) {
        const erroMsg = responseData.itens.find((i: any) => !i.sucesso)?.erro?.mensagem ?? 'Erro ao anexar documento.';
        dispatch(addAlert({ severity: 'error', message: erroMsg }));
      } else {
        const msg = responseData?.mensagem ?? responseData?.message ?? 'Erro ao anexar documento.';
        dispatch(addAlert({ severity: 'error', message: msg }));
      }
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setAttachments([]);
    setUploadKey((prev) => prev + 1);
    onClose();
  };

  const getAttachmentLink = async (doc: CompraVendaDocumento) => {
    try {
      if (doc) {
        const link = document.createElement('a');
        link.href = doc.url;
        link.target = '_blank';
        link.download = doc.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const shortName = (name: string, extension: string) => {
    if (name.length > 20) return `${name.slice(0, 20)}...${extension}`;
    return `${name}${extension}`;
  };

  return (
    <Drawer anchor="right" open={opened} onClose={handleClose} PaperProps={{ style: { padding: '40px', width: 520 } }}>
      <Grid container>
        <Grid item sm={12}>
          <SmSpace />
          <Typography variant="h3">Gestão de Documentos</Typography>
          <Divider variant="fullWidth" />
          <SmSpace />

          {codigoInstrumento && (
            <Typography variant="body2" color="textSecondary">
              {`Instrumento: ${codigoInstrumento}`}
            </Typography>
          )}
          <MdSpace />

          <UploadDocs
            key={uploadKey}
            onChange={handleAttachmentsChange}
            onDelete={handleRemoveAttachment}
            multipleFiles={true}
          />
          <MdSpace />

          {documentos && documentos.length > 0 && (
            <>
              <Typography variant="h6">Documentos Existentes</Typography>
              <SmSpace />
              {documentos.map((doc, i) => (
                <div key={i}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid #C4C4C4',
                      paddingBottom: 40,
                    }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', alignContent: 'flex-end', textAlign: 'right' }}
                    >
                      <InsertDriveFile style={{ marginRight: 10 }} fontSize={'small'} />
                      <div style={{ maxWidth: 300, display: 'flex' }}>
                        <Typography
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          variant="body1"
                        >
                          {shortName(doc.fileName, doc.extension)}
                        </Typography>
                      </div>
                      <Typography style={{ marginLeft: 10 }}>{'-'}</Typography>
                      <Typography style={{ marginLeft: 10 }}>{`${
                        Math.round((doc.size / 1000000) * 100) / 100
                      }MB`}</Typography>
                      <GetApp style={{ marginLeft: 20 }} />
                      <Button onClick={() => getAttachmentLink(doc)} variant="text">
                        {'Download'}
                      </Button>
                    </div>
                  </div>
                  <MdSpace />
                </div>
              ))}
            </>
          )}

          <MdSpace />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleUpload}
            disabled={attachments.length === 0 || uploading}
          >
            {uploading ? 'Enviando...' : 'Enviar Documento'}
          </Button>
          <SmSpace />
          <Button variant="outlined" fullWidth onClick={handleClose}>
            {'Fechar'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};
